import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  styled,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetTherapyByPatientQueryKey,
  useCreateTherapyConditionByPatient,
} from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { formatDateForInput, getCurrentDate } from '../../../../../utils/date.ts';

interface FormValues {
  date: string;
  condition: string;
}
export function AddConditionDialog({
  open,
  onClose,
  patientId,
  therapyId,
}: {
  open: boolean;
  onClose: () => void;
  patientId: string;
  therapyId: string;
}) {
  const queryClient = useQueryClient();
  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: {
      date: formatDateForInput(getCurrentDate()),
      condition: undefined,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const createConditionMutation = useCreateTherapyConditionByPatient({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetTherapyByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim Erfassen des Befindens ist ein Fehler aufgetreten'),
    },
  });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 700 }}>
        <ModalClose />
        <Typography level={'h4'}>Befinden erfassen</Typography>
        <Divider />
        <form
          onSubmit={handleSubmit((date) =>
            createConditionMutation.mutate({
              id: patientId,
              data: {
                therapyId: therapyId,
                date: date.date,
                therapyCondition: date.condition,
              },
            }),
          )}>
          <Stack gap={2}>
            <FormGrid>
              <FormControl error={!!errors.date}>
                <FormLabel>
                  <Typography>Datum *</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="date"
                  rules={{
                    required: true,
                  }}
                />
                {errors.date && <FormHelperText>Datum muss ausgefüllt werden</FormHelperText>}
              </FormControl>
              <FormControl error={!!errors.condition}>
                <FormLabel>
                  <Typography>Befinden *</Typography>
                </FormLabel>
                <Input
                  {...register('condition', {
                    required: true,
                  })}
                />
                {errors.condition && <FormHelperText>Befinden muss angegeben werden</FormHelperText>}
              </FormControl>
            </FormGrid>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={createConditionMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

const FormGrid = styled('div')`
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
`;
